import { cn } from '@/lib/utils';

// Expression pools and playback cadence are adapted from Strobi in
// https://github.com/smontlouis/bible-strong-avatar-lab (AGPL-3.0).
// The SVG projection below is purpose-built for Vyndesk's compact UI surfaces.
type StrobiState = 'sleeping' | 'proud' | 'shy' | 'excited';
type StrobiTone = 'sidebar' | 'adaptive';

type StrobiPose = {
  headX: number;
  headY: number;
  headZ: number;
  widthLeft: number;
  widthRight: number;
  heightLeft: number;
  heightRight: number;
  spacing: number;
  positionYLeft: number;
  positionYRight: number;
  leftAngle: number;
  rightAngle: number;
};

const EXPRESSIONS: Record<string, StrobiPose> = {
  '00': { headX: 7.3, headY: 27.8, headZ: -16.1, widthLeft: 22.5, widthRight: 22.5, heightLeft: 42.38, heightRight: 42.38, spacing: 54.3, positionYLeft: -20.5, positionYRight: -20.5, leftAngle: 0, rightAngle: 0 },
  '02': { headX: -15.29, headY: 15.01, headZ: 12.79, widthLeft: 31.25, widthRight: 31.25, heightLeft: 76.72, heightRight: 76.72, spacing: 68.7, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '03': { headX: 2.95, headY: -16.05, headZ: -20.92, widthLeft: 51.68, widthRight: 51.68, heightLeft: 51.74, heightRight: 51.74, spacing: 70.9, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '04': { headX: 3.4, headY: 13.23, headZ: 8.98, widthLeft: 51.78, widthRight: 51.78, heightLeft: 13.03, heightRight: 13.03, spacing: 63.87, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '08': { headX: -12.3, headY: -17.6, headZ: 5.91, widthLeft: 20.61, widthRight: 20.61, heightLeft: 47.77, heightRight: 47.77, spacing: 54.9, positionYLeft: 0, positionYRight: 0, leftAngle: 23.52, rightAngle: -24.04 },
  '11': { headX: -2.09, headY: -15.9, headZ: -14.47, widthLeft: 34.2, widthRight: 34.2, heightLeft: 85.33, heightRight: 83.18, spacing: 59.41, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '13': { headX: -8.75, headY: -8.74, headZ: -10.77, widthLeft: 56.13, widthRight: 56.13, heightLeft: 15.5, heightRight: 15.16, spacing: 69.28, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '15': { headX: 0.32, headY: 35.31, headZ: -10.9, widthLeft: 22.46, widthRight: 22.46, heightLeft: 39.82, heightRight: 39.82, spacing: 53.9, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '17': { headX: -4.4, headY: 14.07, headZ: -16.13, widthLeft: 19.05, widthRight: 19.05, heightLeft: 43.37, heightRight: 43.37, spacing: 51.73, positionYLeft: 0, positionYRight: 0, leftAngle: 26.29, rightAngle: -20.25 },
  '21': { headX: -5.43, headY: -11.71, headZ: -13.47, widthLeft: 51.4, widthRight: 50.5, heightLeft: 50.1, heightRight: 49.4, spacing: 69, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '22': { headX: 10.29, headY: 3.4, headZ: 7.58, widthLeft: 55.67, widthRight: 55.67, heightLeft: 14.62, heightRight: 14.62, spacing: 68.42, positionYLeft: 0, positionYRight: 0, leftAngle: 0, rightAngle: 0 },
  '24': { headX: 7.13, headY: 7.78, headZ: 3.94, widthLeft: 21.5, widthRight: 23.2, heightLeft: 32, heightRight: 33.5, spacing: 51.2, positionYLeft: 40, positionYRight: 40, leftAngle: 0, rightAngle: 0 },
};

const SEQUENCES: Record<StrobiState, { poses: string[]; holdMs: number; blink: [number, number, number] }> = {
  sleeping: { poses: ['13', '22', '04'], holdMs: 3600, blink: [4800, 6500, 9500] },
  proud: { poses: ['15', '08', '02'], holdMs: 2300, blink: [2100, 2800, 5000] },
  shy: { poses: ['00', '24', '13'], holdMs: 2300, blink: [2100, 2800, 5000] },
  excited: { poses: ['02', '17', '21', '03', '11'], holdMs: 2300, blink: [1200, 1800, 3600] },
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const TRANSITION_MS = 500;
const SOURCE_BODY_SIZE = 240;
const RENDERED_BODY_SIZE = 92;
const GEOMETRY_SCALE = RENDERED_BODY_SIZE / SOURCE_BODY_SIZE;

type EyeGeometry = {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  centerX: number;
  centerY: number;
  angle: number;
};

const eyeGeometry = (pose: StrobiPose, side: 'left' | 'right'): EyeGeometry => {
  const isLeft = side === 'left';
  const spacing = pose.spacing * GEOMETRY_SCALE;
  const width = clamp((isLeft ? pose.widthLeft : pose.widthRight) * GEOMETRY_SCALE, 4, 24);
  const height = clamp((isLeft ? pose.heightLeft : pose.heightRight) * GEOMETRY_SCALE, 3, 30);
  const centerX = 50 + pose.headY * 0.075 + (isLeft ? -spacing / 2 : spacing / 2);
  const centerY = 49 + pose.headX * 0.035 + (isLeft ? pose.positionYLeft : pose.positionYRight) * 0.12;
  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
    width,
    height,
    radius: Math.min(width, height) / 2,
    centerX,
    centerY,
    angle: (isLeft ? pose.leftAngle : pose.rightAngle) + pose.headZ * 0.22,
  };
};

const expandLoop = <T,>(values: T[]) => {
  const expanded: T[] = [values[0]];
  values.forEach((value, index) => {
    expanded.push(value, values[(index + 1) % values.length]);
  });
  return expanded;
};
const numbers = (values: number[]) => expandLoop(values).map(value => value.toFixed(2)).join(';');
const rotations = (values: EyeGeometry[]) =>
  expandLoop(values)
    .map(value => `${value.angle.toFixed(2)} ${value.centerX.toFixed(2)} ${value.centerY.toFixed(2)}`)
    .join(';');

const keyTimesFor = (holdMs: number, frameCount: number) => {
  const total = frameCount * (holdMs + TRANSITION_MS);
  const times = [0];
  for (let index = 0; index < frameCount; index += 1) {
    times.push((index * (holdMs + TRANSITION_MS) + holdMs) / total);
    times.push(((index + 1) * (holdMs + TRANSITION_MS)) / total);
  }
  return times.map(value => value.toFixed(5)).join(';');
};

function AnimatedEye({ frames, duration, keyTimes }: { frames: EyeGeometry[]; duration: string; keyTimes: string }) {
  const first = frames[0];
  const keySplines = Array.from({ length: frames.length * 2 }, () => '0.4 0 0.2 1').join(';');
  const animationProps = { dur: duration, repeatCount: 'indefinite', calcMode: 'spline', keyTimes, keySplines } as const;
  return (
    <rect
      className="strobi-avatar-eye"
      fill="var(--strobi-eyes)"
      x={first.x}
      y={first.y}
      width={first.width}
      height={first.height}
      rx={first.radius}
      transform={`rotate(${first.angle} ${first.centerX} ${first.centerY})`}
    >
      <animate attributeName="x" values={numbers(frames.map(frame => frame.x))} {...animationProps} />
      <animate attributeName="y" values={numbers(frames.map(frame => frame.y))} {...animationProps} />
      <animate attributeName="width" values={numbers(frames.map(frame => frame.width))} {...animationProps} />
      <animate attributeName="height" values={numbers(frames.map(frame => frame.height))} {...animationProps} />
      <animate attributeName="rx" values={numbers(frames.map(frame => frame.radius))} {...animationProps} />
      <animateTransform
        attributeName="transform"
        type="rotate"
        values={rotations(frames)}
        {...animationProps}
      />
    </rect>
  );
}

export default function StrobiAvatar({
  state,
  tone,
  size = 22,
  className,
}: {
  state: StrobiState;
  tone: StrobiTone;
  size?: number;
  className?: string;
}) {
  const sequence = SEQUENCES[state];
  const poses = sequence.poses.map(id => EXPRESSIONS[id]);
  const leftFrames = poses.map(pose => eyeGeometry(pose, 'left'));
  const rightFrames = poses.map(pose => eyeGeometry(pose, 'right'));
  const duration = `${poses.length * (sequence.holdMs + TRANSITION_MS)}ms`;
  const keyTimes = keyTimesFor(sequence.holdMs, poses.length);

  return (
    <span
      className={cn('strobi-avatar', `strobi-avatar--${tone}`, `strobi-avatar--${state}`, className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Strobi ${state}`}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <g>
          <circle className="strobi-avatar-body" fill="var(--strobi-body)" cx="50" cy="50" r="46">
            {state === 'sleeping' && (
              <animate attributeName="r" values="46;45.2;46" dur="4200ms" repeatCount="indefinite" />
            )}
          </circle>
          <AnimatedEye frames={leftFrames} duration={duration} keyTimes={keyTimes} />
          <AnimatedEye frames={rightFrames} duration={duration} keyTimes={keyTimes} />
          <animateTransform
            attributeName="transform"
            type="translate"
            values={state === 'sleeping' ? '0 0;0 0.7;0 0' : '0 0;0 -1;0 0'}
            dur={state === 'sleeping' ? '4200ms' : '3000ms'}
            repeatCount="indefinite"
          />
        </g>
      </svg>
    </span>
  );
}
